// Blog → Edit category. M5, revised 2026-08-15.
//
// Deleting a category does NOT delete its posts: the FK is ON DELETE SET NULL,
// so they survive as uncategorised. Deleting a taxonomy row must never destroy
// author copy.

import { Button, Card, Field, Input } from '@fajrarabi/ui';
import { blogSlug } from '@fajrarabi/zod-schemas/blog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { ConfirmDialog } from '~/components';
import { ApiError } from '~/lib/api';
import { deleteCategory, getCategory, updateCategory } from '~/lib/blog';

export const Route = createFileRoute('/_authed/blog-categories/$id')({
  component: EditBlogCategoryPage,
});

function EditBlogCategoryPage() {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const categoryQuery = useQuery({
    queryKey: ['blog', 'category', id],
    queryFn: ({ signal }) => getCategory(id, signal),
  });

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    const category = categoryQuery.data;
    if (!category || seeded.current) return;
    seeded.current = true;
    setName(category.name);
    setSlug(category.slug);
    setIsActive(category.isActive);
  }, [categoryQuery.data]);

  const saveMut = useMutation({
    mutationFn: () => updateCategory(id, { name: name.trim(), slug: slug.trim(), isActive }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog', 'categories'] });
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      setError(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog', 'categories'] });
      void qc.invalidateQueries({ queryKey: ['blog', 'posts'] });
      void navigate({ to: '/blog-categories' });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not delete.');
    },
  });

  if (categoryQuery.isLoading) return <p className="p-4 text-sm text-slate-500">Loading…</p>;
  if (categoryQuery.isError || !categoryQuery.data) {
    return (
      <p role="alert" className="p-4 text-sm text-rose-700">
        Could not load this category.
      </p>
    );
  }

  const slugValid = blogSlug.safeParse(slug.trim()).success;

  return (
    <div className="mx-auto max-w-2xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => void navigate({ to: '/blog-categories' })}>
            ← All categories
          </Button>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Edit category</h1>
        </div>
        {saved && <span className="text-sm text-emerald-700">Saved</span>}
      </header>

      {error && (
        <p role="alert" className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </p>
      )}

      <Card className="mt-6">
        <div className="p-4">
          <Field label="Name">
            {(fieldId) => (
              <Input
                id={fieldId}
                dir="auto"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Slug"
            className="mt-4"
            hint="Changing this breaks any existing ?category= link."
            error={
              slug.trim() && !slugValid
                ? 'Lowercase letters, Arabic script, digits and hyphens only.'
                : undefined
            }
          >
            {(fieldId) => (
              <Input
                id={fieldId}
                dir="auto"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
            )}
          </Field>

          <label className="mt-4 flex items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            Visible on the site
          </label>
          <p className="mt-1 text-xs text-slate-500">
            Hiding a category removes it from the blog’s filter list. Its posts stay published.
          </p>

          <div className="mt-6 flex gap-2">
            <Button
              onClick={() => saveMut.mutate()}
              disabled={!name.trim() || !slugValid || saveMut.isPending}
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={() => void navigate({ to: '/blog-categories' })}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>

      <Card className="mt-6">
        <div className="p-4">
          <Button
            variant="ghost"
            className="w-full text-error-700"
            onClick={() => setConfirmingDelete(true)}
          >
            Delete category
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Delete this category"
        description={
          <>
            <span className="font-medium">{name}</span> will be removed. Posts in it are NOT deleted
            — they simply become uncategorised.
          </>
        }
        confirmLabel="Delete category"
        tone="danger"
        onConfirm={async () => {
          await deleteMut.mutateAsync();
        }}
      />
    </div>
  );
}
